import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { Heart, Camera, ExternalLink, Mail, AlertCircle } from 'lucide-react'

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About G6 Optics - Affiliate Disclosure | Mini Camera Enthusiast Site</title>
        <meta name="description" content="Learn about G6 Optics, an independent affiliate website created by photography enthusiasts. We are not affiliated with the G6 camera brand - just passionate fans sharing our love for retro mini cameras." />
        <link rel="canonical" href="https://g6optics.com/about" />
        <meta name="robots" content="noindex" />
      </Helmet>

      <article className="legal-page">
        <div className="container">
          <header className="legal-header">
            <h1>About G6 Optics</h1>
            <p className="legal-subtitle">An independent fan site and affiliate resource for mini camera enthusiasts</p>
          </header>

          <div className="legal-content">
            {/* Affiliate Disclosure Box */}
            <div className="disclosure-box">
              <div className="disclosure-icon">
                <AlertCircle size={24} />
              </div>
              <div className="disclosure-content">
                <h2>Important Affiliate Disclosure</h2>
                <p>
                  <strong>G6 Optics is an independent affiliate website.</strong> We are not affiliated with, 
                  endorsed by, or officially connected to the manufacturers or brands of the G6 camera or any 
                  other products featured on this site. All product names, logos, and trademarks are the property 
                  of their respective owners.
                </p>
                <p>
                  This website participates in the Amazon Associates Program, an affiliate advertising program 
                  designed to provide a means for sites to earn advertising fees by linking to Amazon.com. When 
                  you click on product links and make a purchase, we may earn a small commission at no additional 
                  cost to you.
                </p>
              </div>
            </div>

            {/* Who We Are */}
            <section className="legal-section">
              <div className="section-icon">
                <Heart size={20} />
              </div>
              <h2>Who We Are</h2>
              <p>
                G6 Optics was created by photography enthusiasts who fell in love with the unique aesthetic 
                of CCD sensor cameras and the resurgence of retro-style digital photography. After discovering 
                the G6 thumb camera and experiencing its authentic vintage look firsthand, we wanted to create 
                a resource for others who share our passion for this niche corner of photography.
              </p>
              <p>
                We're not professional reviewers or industry insiders—we're everyday photographers who appreciate 
                the charm of imperfect, character-filled images that modern smartphone cameras simply cannot replicate. 
                This site is our way of sharing that enthusiasm and helping others discover the joy of shooting with 
                dedicated mini digital cameras.
              </p>
            </section>

            {/* Our Mission */}
            <section className="legal-section">
              <div className="section-icon">
                <Camera size={20} />
              </div>
              <h2>Our Mission</h2>
              <p>
                Our goal is to provide honest, helpful information about mini digital cameras, retro photography, 
                and the G6 thumb camera specifically. We aim to:
              </p>
              <ul className="legal-list">
                <li>Share genuine insights based on our personal experience with these cameras</li>
                <li>Help potential buyers make informed decisions about whether a mini camera is right for them</li>
                <li>Create a resource for the growing community of retro digital photography enthusiasts</li>
                <li>Provide SEO-optimized content to help people discover this wonderful niche</li>
              </ul>
            </section>

            {/* How We Earn */}
            <section className="legal-section">
              <div className="section-icon">
                <ExternalLink size={20} />
              </div>
              <h2>How We Earn Revenue</h2>
              <p>
                Transparency is important to us. Here's exactly how this website generates income:
              </p>
              <ul className="legal-list">
                <li>
                  <strong>Amazon Affiliate Links:</strong> Product links on this site are affiliate links to 
                  Amazon.com. If you click through and make a purchase, we receive a small percentage of the 
                  sale (typically 1-4%). This doesn't increase the price you pay.
                </li>
                <li>
                  <strong>No Paid Reviews:</strong> We have never accepted payment in exchange for positive 
                  reviews. Our opinions are our own, based on personal use and research.
                </li>
                <li>
                  <strong>No Brand Sponsorship:</strong> We are not sponsored by any camera manufacturer. 
                  The G6 cameras featured on this site were purchased with our own money.
                </li>
              </ul>
              <p>
                The affiliate income helps us maintain this website, cover hosting costs, and continue creating 
                content for the photography community. We only recommend products we genuinely believe in.
              </p>
            </section>

            {/* Editorial Independence */}
            <section className="legal-section">
              <h2>Editorial Independence</h2>
              <p>
                Our affiliate relationships do not influence our editorial content. We strive to provide balanced, 
                honest assessments of the products we feature. If a product has drawbacks, we'll mention them. 
                Our priority is helping you make the right decision for your needs, even if that means recommending 
                against a purchase.
              </p>
              <p>
                The G6 thumb camera isn't perfect for everyone—it's a niche product with specific strengths and 
                limitations. We try to be upfront about who will love it and who might be better served by 
                different options.
              </p>
            </section>

            {/* Contact */}
            <section className="legal-section">
              <div className="section-icon">
                <Mail size={20} />
              </div>
              <h2>Contact Us</h2>
              <p>
                Have questions, feedback, or just want to share your own G6 photos? We'd love to hear from you. 
                While we're a small operation and may not respond immediately, we read every message.
              </p>
              <p>
                For general inquiries, please reach out through our social channels or leave feedback through 
                the contact methods available on our main site.
              </p>
            </section>

            {/* Legal Links */}
            <section className="legal-section">
              <h2>Legal Information</h2>
              <p>
                For more information about how we handle your data and the terms of using this website, 
                please review our legal policies:
              </p>
              <div className="legal-links">
                <Link to="/privacy" className="legal-link">Privacy Policy</Link>
                <Link to="/terms" className="legal-link">Terms of Service</Link>
              </div>
            </section>

            {/* Last Updated */}
            <footer className="legal-footer">
              <p>Last updated: January 2026</p>
              <p>
                Thank you for visiting G6 Optics. Your support through our affiliate links helps keep this 
                passion project alive. Happy shooting!
              </p>
            </footer>
          </div>
        </div>
      </article>
    </>
  )
}
